import { AxiosInstance } from 'axios';
import {
  ListSeriesRequest,
  ListSeriesResponse,
  Series
} from '../types/series';
import { buildOffsetParams } from '../util/pagination';
import {
  ListSeriesRequestSchema,
  ListSeriesResponseSchema,
  SeriesSchema
} from '../z/series';

export class SeriesClient {
  constructor(
    private readonly http: AxiosInstance
    
  ) {}

  /**
   * List all series with optional filtering and pagination.
   * 
   * This endpoint returns a paginated list of series with optional filtering
   * by active status and category. Series are collections of related events
   * and markets, useful for organizing content by themes or topics.
   * 
   * @example
   * ```typescript
   * // Get active series
   * const series = await client.series.list({ 
   *   active: true, 
   *   limit: 20 
   * });
   * 
   * // Get series by category
   * const politicsSeries = await client.series.list({ 
   *   category: 'politics',
   *   active: true 
   * });
   * ```
   * 
   * @param params - Optional query parameters for filtering and pagination
   * @param params.limit - Number of series to return (default: 10)
   * @param params.offset - Number of series to skip (default: 0)
   * @param params.active - Filter by active status (true/false)
   * @param params.category - Filter by category
   * 
   * @returns Promise<ListSeriesResponse> - Paginated list of series
   * @throws {Error} When the API request fails or returns invalid data
   * 
   * @see {@link https://docs.polymarket.com/api-reference/series/list-series} - API Documentation
   */
  async list(params?: ListSeriesRequest): Promise<ListSeriesResponse> {
    const validatedParams = params ? ListSeriesRequestSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);
            try {
      const response = await this.http.get('/series', { params: queryParams });
      const validatedData = ListSeriesResponseSchema.parse(response.data);
      
            return validatedData as ListSeriesResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list series: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a series by its unique identifier.
   * 
   * This endpoint returns detailed information about a specific series,
   * including its associated events and markets. Series are collections
   * of related content organized by themes or topics.
   * 
   * @example
   * ```typescript
   * const series = await client.series.getById('123');
   * console.log('Series Title:', series.title);
   * console.log('Category:', series.category);
   * console.log('Events:', series.events.length);
   * console.log('Markets:', series.markets.length);
   * ```
   * 
   * @param id - Series ID
   * @returns Promise<Series> - Series data with events and markets
   * @throws {Error} When the series is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/series/get-series-by-id} - API Documentation
   */
  async getById(id: string): Promise<Series> {
            try {
      const response = await this.http.get(`/series/${id}`);
      const validatedData = SeriesSchema.parse(response.data);
      
            return validatedData as Series;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get series by ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }
}
